import { create } from 'zustand';
import {
  mockAutomationProfiles,
  mockHardRules,
  mockPreferences,
  mockSoftRules,
  mockTeacherPreferences,
} from '../lib/mockData/rules';
import type { AutomationProfile, HardRule, Preference, SoftRule, TeacherPreference } from '../types';

interface RulesState {
  // Data
  hardRules: HardRule[];
  softRules: SoftRule[];
  preferences: Preference[];
  teacherPreferences: TeacherPreference[];
  automationProfiles: AutomationProfile[];

  // Hard Rule operations
  addHardRule: (rule: HardRule) => void;
  updateHardRule: (id: string, data: Partial<HardRule>) => void;
  deleteHardRule: (id: string) => void;
  toggleHardRuleActive: (id: string) => void;

  // Soft Rule operations
  addSoftRule: (rule: SoftRule) => void;
  updateSoftRule: (id: string, data: Partial<SoftRule>) => void;
  deleteSoftRule: (id: string) => void;
  toggleSoftRuleActive: (id: string) => void;
  updateSoftRuleWeight: (id: string, weight: number) => void;

  // Preference operations
  addPreference: (preference: Preference) => void;
  updatePreference: (id: string, data: Partial<Preference>) => void;
  deletePreference: (id: string) => void;
  togglePreferenceActive: (id: string) => void;
  reorderPreferences: (preferences: Preference[]) => void;

  // Teacher Preference operations
  addTeacherPreference: (preference: TeacherPreference) => void;
  updateTeacherPreference: (id: string, data: Partial<TeacherPreference>) => void;
  deleteTeacherPreference: (id: string) => void;
  approveTeacherPreference: (id: string, reviewedBy: string, notes?: string) => void;
  rejectTeacherPreference: (id: string, reviewedBy: string, notes: string) => void;

  // Automation Profile operations
  addAutomationProfile: (profile: AutomationProfile) => void;
  updateAutomationProfile: (id: string, data: Partial<AutomationProfile>) => void;
  deleteAutomationProfile: (id: string) => void;
  setDefaultAutomationProfile: (id: string) => void;
  toggleAutomationProfileActive: (id: string) => void;
}

export const useRulesStore = create<RulesState>((set, _get) => ({
  // Initial state
  hardRules: mockHardRules,
  softRules: mockSoftRules,
  preferences: mockPreferences,
  teacherPreferences: mockTeacherPreferences,
  automationProfiles: mockAutomationProfiles,

  // Hard Rule operations
  addHardRule: (rule) =>
    set((state) => ({
      hardRules: [...state.hardRules, rule],
    })),

  updateHardRule: (id, data) =>
    set((state) => ({
      hardRules: state.hardRules.map((rule) => (rule.id === id ? { ...rule, ...data, updatedAt: new Date() } : rule)),
    })),

  deleteHardRule: (id) =>
    set((state) => ({
      hardRules: state.hardRules.filter((rule) => rule.id !== id),
    })),

  toggleHardRuleActive: (id) =>
    set((state) => ({
      hardRules: state.hardRules.map((rule) =>
        rule.id === id ? { ...rule, isActive: !rule.isActive, updatedAt: new Date() } : rule,
      ),
    })),

  // Soft Rule operations
  addSoftRule: (rule) =>
    set((state) => ({
      softRules: [...state.softRules, rule],
    })),

  updateSoftRule: (id, data) =>
    set((state) => ({
      softRules: state.softRules.map((rule) => (rule.id === id ? { ...rule, ...data, updatedAt: new Date() } : rule)),
    })),

  deleteSoftRule: (id) =>
    set((state) => ({
      softRules: state.softRules.filter((rule) => rule.id !== id),
    })),

  toggleSoftRuleActive: (id) =>
    set((state) => ({
      softRules: state.softRules.map((rule) =>
        rule.id === id ? { ...rule, isActive: !rule.isActive, updatedAt: new Date() } : rule,
      ),
    })),

  updateSoftRuleWeight: (id, weight) =>
    set((state) => ({
      softRules: state.softRules.map((rule) => (rule.id === id ? { ...rule, weight, updatedAt: new Date() } : rule)),
    })),

  // Preference operations
  addPreference: (preference) =>
    set((state) => ({
      preferences: [...state.preferences, preference],
    })),

  updatePreference: (id, data) =>
    set((state) => ({
      preferences: state.preferences.map((pref) =>
        pref.id === id ? { ...pref, ...data, updatedAt: new Date() } : pref,
      ),
    })),

  deletePreference: (id) =>
    set((state) => ({
      preferences: state.preferences.filter((pref) => pref.id !== id),
    })),

  togglePreferenceActive: (id) =>
    set((state) => ({
      preferences: state.preferences.map((pref) =>
        pref.id === id ? { ...pref, isActive: !pref.isActive, updatedAt: new Date() } : pref,
      ),
    })),

  reorderPreferences: (preferences) =>
    set(() => ({
      preferences: preferences.map((pref, index) => ({
        ...pref,
        rank: index + 1,
        updatedAt: new Date(),
      })),
    })),

  // Teacher Preference operations
  addTeacherPreference: (preference) =>
    set((state) => ({
      teacherPreferences: [...state.teacherPreferences, preference],
    })),

  updateTeacherPreference: (id, data) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.map((pref) => (pref.id === id ? { ...pref, ...data } : pref)),
    })),

  deleteTeacherPreference: (id) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.filter((pref) => pref.id !== id),
    })),

  approveTeacherPreference: (id, reviewedBy, notes) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.map((pref) =>
        pref.id === id
          ? {
              ...pref,
              status: 'approved',
              reviewedAt: new Date(),
              reviewedBy,
              notes: notes || pref.notes,
            }
          : pref,
      ),
    })),

  rejectTeacherPreference: (id, reviewedBy, notes) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.map((pref) =>
        pref.id === id
          ? {
              ...pref,
              status: 'rejected',
              reviewedAt: new Date(),
              reviewedBy,
              notes,
            }
          : pref,
      ),
    })),

  // Automation Profile operations
  addAutomationProfile: (profile) =>
    set((state) => ({
      automationProfiles: [...state.automationProfiles, profile],
    })),

  updateAutomationProfile: (id, data) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.map((profile) =>
        profile.id === id ? { ...profile, ...data, updatedAt: new Date() } : profile,
      ),
    })),

  deleteAutomationProfile: (id) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.filter((profile) => profile.id !== id),
    })),

  setDefaultAutomationProfile: (id) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.map((profile) => ({
        ...profile,
        isDefault: profile.id === id,
        updatedAt: new Date(),
      })),
    })),

  toggleAutomationProfileActive: (id) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.map((profile) =>
        profile.id === id ? { ...profile, isActive: !profile.isActive, updatedAt: new Date() } : profile,
      ),
    })),
}));
